import {
  BTypeMainHead,
  BTypeCardView,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  colors,
  ATypeMap,
  CTypeBreadCrumb,
  FullWidthButton,
  GOI_WEB_URL,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";
import facilities from "goi_common/src/constants/facilities_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((f) => targetSidos.includes(f.sido) && f.is_public)
    .map((f) => ({
      params: {
        companyname: f.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.companyname as string;
  const companyname = code;
  let p_facility;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    p_facility = facilities.find((f) => f.companyname === companyname);
    if (p_facility) {
      name = p_facility.companyname;

      district = p_facility.address.slice(0, 2);
      if (p_facility.address.split(" ")[0].length == 4)
        district = p_facility.address[0] + p_facility.address[2];

      description = `강원장례정보공개 (고이장례연구소) - ${p_facility.companyname}  | 기초생활수급자 안치 비용`;
    }
  }

  return {
    props: {
      defaultPFacility: p_facility,
      pageDescription: description,
      pageKeywords: `${p_facility?.companyname}, ${p_facility?.companyname} 기초생활수급자, ${district} 기초생활수급자 장례, 장제비, 장제급여, 기초생활수급자 장례 얼마,`,
      pageOgTitle: `${p_facility?.companyname}에서의 기초생활 보호 대상자를 위한 공설 장지 비용 정보를 안내합니다 :: 강원장례정보공개 (고이장례연구소)`,
      pageOgDescription: description,
      pageOgImage: p_facility?.images[0].url || "",
      hasError: !p_facility,
    },
  };
};

export default function companyname({
  defaultPFacility,
  pageOgTitle,
}: {
  defaultPFacility: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionPFacilities = facilities.filter((p) =>
    targetSidos.includes(String(p.sido)) && p.is_public
  );
  const sidoName = FUNERAL_SIDO.get(Number(defaultPFacility.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultPFacility.sido))?.get(
    Number(defaultPFacility.sigungu)
  );
  const carouselItems = regionPFacilities.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.gangwon-portal.co.kr/gangwon-benefit/facility/${res.companyname}`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionPFacilities.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionPFacilities.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>
      <CTypeBreadCrumb
        firstItem={{
          text: `기초수급자장례 추천`,
          href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
          event: {
            domain: "gangwon",
            path: "benef_facility",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `기초생활수급자 장례헬프센터`,
          href: `/gangwon-benefit/`,
          event: {
            domain: "gangwon",
            path: "benef_facility",
            trigger_and_target: "br_crumb_benef_facility",
          },
        }}
        goiHomeEvent={{
          domain: "gangwon",
          path: "benef_facility",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <BTypeMainHead
        sidoName={sidoName}
        sigunguName={sigunguName}
        mainColor={regionColor}
        defaultParlor={defaultPFacility}
        title={`${defaultPFacility.companyname}`}
        managed_type={defaultPFacility.managed_type}
        isJangji
      />

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <Stack
          className="body_16_m"
          style={{
            background: colors.gray50,
            padding: "12px 14px",
            margin: "0 20px",
          }}
        >
          기초생활수급자는 화장 비용이 전액 감면됩니다.
        </Stack>

        <Stack padding="18px 20px 40px" gap="40px">
          <Box className="body_14_m">
            e-하늘장사정보시스템에서 가져온 정보이며, 자세한 안치 대상 여부를
            알아보려면 상담이 필요합니다.
          </Box>
          <FullWidthButton
            text="바로 상담하기"
            href="tel:1666-9784"
            backgroundColor={regionColor.c400}
            event={{
              domain: "gangwon",
              path: "benef_facility",
              trigger_and_target: "goi_call",
            }}
          />
        </Stack>
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <ATypeMap defaultParlor={defaultPFacility} />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <h3 className="subtitle_18_b">강원 기초생활수급자 장지</h3>
        {regionPFacilities.map((res) => (
          <BTypeCardView
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/gangwon-benefit/facility/${res.companyname}/`}
            // descriptions={["기초생활수급자 무료"]}
            descriptions={[""]}
            imgSrc={res.images[0]?.url ? res.images[0].url : ""}
          />
        ))}
      </Stack>
    </Stack>
  );
}
